<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('school.academic.setup.program.grade.grade') }}
      </h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('school.academic.setup.program.grade.grade'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="row">
          <div class="col-sm-12 col-md-10">
            <ts-button
              color="info"
              outline
              @click="$router.push({ name: 'grade-create' })"
            >
              {{ $t('add_new') }}
            </ts-button>
            <ts-button
              color="info"
              class="ml-2"
              outline
              @click="defineNextGrade"
            >
              {{ $t('school.academic.setup.program.grade.next_grade') }}
            </ts-button>
            <ts-button
              color="info"
              class="ml-2"
              outline
              @click="subjectOutline"
            >
              {{ $t('school.academic.setup.program.grade.subjectOutline') }}
            </ts-button>
          </div>
          <div class="col-sm-12 col-md-2 d-flex justify-content-end">
            <ts-input v-model="search" :placeholder="$t('search')" />
          </div>
        </div>
      </ts-card-wrapper>
      <ts-table
        :columns="columns"
        :records="resources"
        :loading="loading"
        :rowGroup="{
          field: 'level.level_with_sub'
        }"
      >
        <template v-slot="{ record }">
          <td class="kh-font ts-whitespace-no-wrap">
            {{
              (record.grade_name_native
                ? record.grade_name_native + ' | '
                : '') + record.grade_name_foreign
            }}
          </td>
          <td class="ts-whitespace-no-wrap">{{ record.next_grade_name }}</td>
          <td class="text-center ts-whitespace-no-wrap">
            {{ record.grade_order }}
          </td>
          <td class="ts-whitespace-no-wrap">{{ record.grade_abbr }}</td>
          <td class="ts-whitespace-no-wrap">
            {{
              record.score_type == 1
                ? 'Numbering'
                : record.score_type == 2
                ? 'Grading'
                : 'Rating'
            }}
          </td>
          <td class="ts-whitespace-no-wrap">
            {{
              record.monthly_score_sc_bas_id == 1 ? 'Item Base' : 'Subject Base'
            }}
          </td>
          <td class="ts-whitespace-no-wrap">
            {{
              record.semester_score_sc_bas_id == 1
                ? 'Item Base'
                : 'Subject Base'
            }}
          </td>
          <td class="ts-whitespace-no-wrap">
            {{
              record.level
                ? record.level.sub_program
                  ? record.level.sub_program.main_program
                    ? record.level.sub_program.main_program.acp_name_foreign
                      ? record.level.sub_program.main_program.acp_name_foreign
                      : ''
                    : ''
                  : ''
                : ''
            }}
          </td>
          <td class="ts-whitespace-no-wrap">
            {{
              record.level
                ? record.level.sub_program
                  ? record.level.sub_program.acps_name_foreign
                    ? record.level.sub_program.acps_name_foreign
                    : ''
                  : ''
                : ''
            }}
          </td>
          <td class="text-center">
            <a
              href="#"
              v-tooltip="$t('show')"
              class="mr-2 text-info"
              @click.prevent="show(record)"
            >
              <i class="fas fa-eye"></i>
            </a>
            <a
              href="#"
              v-tooltip="$t('edit')"
              class="mr-2 text-primary"
              @click.prevent="edit(record)"
            >
              <i class="fas fa-edit"></i>
            </a>
            <a
              href="#"
              v-tooltip="$t('delete')"
              class="text-danger"
              @click.prevent="destroy(record)"
              :disabled="record._deleting"
            >
              <i class="fas fa-circle-notch spin" v-if="record._deleting"></i>
              <i class="fas fa-trash" v-else></i>
            </a>
          </td>
        </template>
      </ts-table>
      <div class="d-flex justify-content-between p-15">
        <h1 style="margin-top: 11px" class="ts-text-gray-500">
          <b
            ><i
              ><span class="text-danger">* </span>
              {{ $t('school.academic.setup.program.grade.note') }} :
            </i></b
          >
          {{
            $t(
              'school.academic.setup.program.grade.only_user_at_head_office_can_modify_or_delete'
            )
          }}
        </h1>
        <ts-pagination v-model="pagination" @navigate="fetchData">
        </ts-pagination>
      </div>
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import NextGrade from './next-grade'
import SubjectOutline from './components/subject-outline.vue'

export default {
  name: 'grade',
  data () {
    return {
      loading: false
    }
  },
  computed: {
    ...mapState('grade', ['resources', 'pagination']),
    search: {
      get () {
        return this.$store.state.grade.search
      },
      set (newValue) {
        this.$store.commit('grade/SET_SEARCH', newValue)
        this.$store.commit('grade/RESET_CURRENT_PAGE')
      }
    },
    columns () {
      return [
        {
          name: this.$t('school.academic.setup.program.grade.gradeName')
        },
        {
          name: this.$t('school.academic.setup.program.grade.next_grade')
        },
        {
          name: this.$t('school.academic.setup.program.grade.grade_order'),
          style: 'width:7%; text-align:center'
        },
        {
          name: this.$t('school.academic.setup.program.grade.abbreviation'),
          style: 'width:12%'
        },
        {
          name: this.$t('school.academic.setup.program.grade.scoring_type'),
          style: 'width:10%'
        },
        {
          name: this.$t('school.academic.setup.program.grade.monthlyScore'),
          style: 'width:10%'
        },
        {
          name: this.$t('school.academic.setup.program.grade.semesterScore'),
          style: 'width:10%'
        },
        {
          name: this.$t('school.academic.setup.program.grade.mainProgram'),
          style: 'width:10%'
        },
        {
          name: this.$t('school.academic.setup.program.grade.subProgram'),
          style: 'width:10%'
        },
        {
          name: this.$t('school.academic.setup.program.grade.actions'),
          style: 'text-align:center; width:7%'
        }
      ]
    }
  },
  created () {
    this.fetchData()
  },
  methods: {
    fetchData (attributes) {
      this.loading = true
      this.$store
        .dispatch('grade/fetch', attributes)
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    defineNextGrade () {
      this.$modal.show(
        NextGrade,
        {},
        {
          height: 'auto',
          width: '30%',
          scrollable: true,
          clickToClose: false
        }
      )
    },
    edit (record) {
      this.$store.commit('grade/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'grade-edit',
        params: { id: record.grade_id }
      })
    },
    show (record) {
      this.$store.commit('grade/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'grade-show',
        params: { id: record.grade_id }
      })
    },
    async destroy (record) {
      if (await this.$confirmModal.danger('Are you sure to delete ?')) {
        record._deleting = true
        this.$store
          .dispatch('grade/destroy', record.grade_id)
          .then(response => {
            this.fetchData()
            this.$toasted.success(response.message)
            record._deleting = false
          })
          .catch(error => {
            this.$toasted.error(error.message)
            record._deleting = false
          })
      }
    },
    subjectOutline () {
      this.$modal.show(
        SubjectOutline,
        {
          data: {}
        },
        {
          height: 'auto',
          width: '80%',
          scrollable: true,
          draggable: '.form-header',
          clickToClose: false
        }
      )
    },
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData({ search })
    }, 500)
  },
  beforeRouteLeave (to, from, next) {
    if (_.isEmpty(to.params) && to.name != 'grade-create') {
      this.$store.commit('grade/RESET_STATE')
    }
    next()
  }
}
</script>
